// services/lordsApi.ts
// API calls for the Lords endpoints (user-settings, transfers, exposure)
import { apiFetch } from '../utils/api';

// ─── User Settings ──────────────────────────────────────────────

export const updatePassword = (payload: {
  userId: number | string;
  userType: 'STAFF' | 'USER';
  newPassword: string;
  transactionPassword: string;
}) => apiFetch('/lords/user-setting/update-password', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const updateStatus = (payload: {
  userId: number | string;
  userType: 'STAFF' | 'USER';
  status?: string;
  betStatus?: string;
  sportsLocked?: boolean;
  casinoLocked?: boolean;
  systemLocked?: boolean;
  transactionPassword: string;
}) => apiFetch('/lords/user-setting/status', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const updateCreditLimit = (payload: {
  userId: number | string;
  userType: 'STAFF' | 'USER';
  creditLimit: number;
  transactionPassword: string;
}) => apiFetch('/lords/user-setting/credit-limit', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const updateExposureLimit = (payload: {
  userId: number | string;
  userType: 'STAFF' | 'USER';
  exposureLimit: number | null;
  transactionPassword: string;
}) => apiFetch('/lords/user-setting/exposure-limit', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

// ─── G/T Transfer ───────────────────────────────────────────────

export const gtTransfer = (payload: {
  userId: number | string;
  amount: number;
  recieverType: string;
  transactionPassword: string;
}) => apiFetch('/lords/transfer', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const getTransferStatement = (params: { page?: number; limit?: number }) =>
  apiFetch(`/lords/transfer/statement?page=${params.page || 1}&limit=${params.limit || 25}`);

// ─── Casino Transfer ────────────────────────────────────────────

export const casinoTransfer = (payload: {
  userId: number | string;
  amount: number;
  recieverType: string;
  transactionPassword: string;
}) => apiFetch('/lords/casino-transfer', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

export const getCasinoTransferStatement = (params: { page?: number; limit?: number }) =>
  apiFetch(`/lords/casino-transfer/statement?page=${params.page || 1}&limit=${params.limit || 25}`);

// ─── Net Exposure ───────────────────────────────────────────────

export const getNetExposure = (userId: number | string, userType: 'STAFF' | 'USER') =>
  apiFetch(`/lords/net-exposure/sports?userId=${userId}&userType=${userType}`);

// ─── Agent Listing ──────────────────────────────────────────────

export const getAllDetails = (params: {
  page?: number;
  limit?: number;
  search?: string;
  parentId?: number;
}) => {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  if (params.parentId) qs.set('parentId', String(params.parentId));
  return apiFetch(`/lords/users/all-details?${qs.toString()}`);
};

// ─── Current User ───────────────────────────────────────────────

export const updateCurrent = () => apiFetch('/lords/update-current', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({}),
});

// ─── Create Staff (with transaction password) ───────────────────

export const createStaff = (payload: {
  username: string;
  email: string;
  password: string;
  role: string;
  credit: number;
  percentage: number;
  transactionPassword: string;
}) => apiFetch('/api/staff', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: payload.username,
    email: payload.email,
    password: payload.password,
    role_name: payload.role,
    initial_balance: payload.credit,
    percentage: payload.percentage,
    transactionPassword: payload.transactionPassword,
  }),
});

export const createUser = (payload: {
  username: string;
  email: string;
  password: string;
  credit: number;
  percentage: number;
  transactionPassword: string;
}) => apiFetch('/api/staff/players', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    username: payload.username,
    email: payload.email,
    password: payload.password,
    initial_balance: payload.credit,
    transactionPassword: payload.transactionPassword,
  }),
});

// ─── First Login ────────────────────────────────────────────────

export const firstLoginPassword = (staffId: number | string, newPassword: string) =>
  apiFetch('/api/staff/auth/first-login-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffId, newPassword }),
  });
